import React from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, DollarSign, Clock, TrendingDown } from 'lucide-react';
import { Abonnement } from '../types/abonnement';
import { formatXOF, calculateFees, PROVIDERS, getProviderFees } from '../utils/feeCalculator';
import { Provider, Currency } from '../types/calculator';
interface AbonnementDetailModalProps {
  abonnement: Abonnement;
  onClose: () => void;
}
interface ProviderComparison {
  provider: Provider;
  totalCost: number;
  fxRate: number;
  displayedFxRate: number;
  fee: number | 'not_defined';
}
export function AbonnementDetailModal({
  abonnement,
  onClose
}: AbonnementDetailModalProps) {
  const currency = abonnement.currency as Currency;
  const amount = abonnement.amount;
  // Calculate days until payment
  const calculateDaysUntil = () => {
    const today = new Date();
    const next = new Date(abonnement.next_payment_date);
    const diff = Math.ceil((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const daysUntil = calculateDaysUntil();
  const isUrgent = daysUntil <= 7 && daysUntil >= 0;
  const isPast = daysUntil < 0;
  // Calculate comparison for all providers
  const calculateComparison = (): ProviderComparison[] => {
    const providers = Object.keys(PROVIDERS) as Provider[];
    const fees = getProviderFees();
    return providers.map(provider => {
      const breakdown = calculateFees(amount, currency, provider);
      const providerFee = fees[provider];
      const feeAmount = providerFee === 'not_defined' ? 0 : providerFee;
      return {
        provider,
        totalCost: breakdown.totalCost + feeAmount,
        fxRate: breakdown.fxRate,
        displayedFxRate: breakdown.displayedFxRate,
        fee: providerFee
      };
    }).sort((a, b) => a.totalCost - b.totalCost);
  };
  const comparisons = calculateComparison();
  const bestProvider = comparisons[0];
  const savings = comparisons[comparisons.length - 1].totalCost - bestProvider.totalCost;
  const formatAmount = (amount: number, currency: Currency) => {
    if (currency === 'XOF') {
      return formatXOF(amount);
    } else if (currency === 'EUR') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-6">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-white rounded-2xl shadow-xl max-w-3xl w-full my-8" data-id="element-7">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl" data-id="element-8">
          <div className="flex justify-between items-start" data-id="element-9">
            <div data-id="element-10">
              <h2 className="text-2xl font-bold mb-2" data-id="element-11">{abonnement.name}</h2>
              <p className="text-blue-100" data-id="element-12">Détails de l'abonnement</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-13">
              <X className="w-6 h-6" data-id="element-14" />
            </button>
          </div>
        </div>

        <div className="p-6" data-id="element-15">
          {/* Payment Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-16">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-17">
              <div className="flex items-center gap-2 text-blue-700 mb-2" data-id="element-18">
                <DollarSign className="w-5 h-5" data-id="element-19" />
                <span className="text-sm font-medium" data-id="element-20">Montant</span>
              </div>
              <p className="text-2xl font-bold text-blue-900" data-id="element-21">
                {formatAmount(amount, currency)}
              </p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-id="element-22">
              <div className="flex items-center gap-2 text-emerald-700 mb-2" data-id="element-23">
                <Calendar className="w-5 h-5" data-id="element-24" />
                <span className="text-sm font-medium" data-id="element-25">Prochain paiement</span>
              </div>
              <p className="text-lg font-bold text-emerald-900" data-id="element-26">
                {new Date(abonnement.next_payment_date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
              </p>
            </div>

            <div className={`rounded-lg p-4 border ${isPast ? 'bg-red-50 border-red-200' : isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-27">
              <div className={`flex items-center gap-2 mb-2 ${isPast ? 'text-red-700' : isUrgent ? 'text-orange-700' : 'text-gray-700'}`} data-id="element-28">
                <Clock className="w-5 h-5" data-id="element-29" />
                <span className="text-sm font-medium" data-id="element-30">Temps restant</span>
              </div>
              <p className={`text-2xl font-bold ${isPast ? 'text-red-900' : isUrgent ? 'text-orange-900' : 'text-gray-900'}`} data-id="element-31">
                {isPast ? `${Math.abs(daysUntil)} jour${Math.abs(daysUntil) > 1 ? 's' : ''} de retard` : daysUntil === 0 ? "Aujourd'hui" : `${daysUntil} jour${daysUntil > 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          {/* Best Provider Highlight */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-xl p-6 mb-6" data-id="element-32">
            <div className="flex items-start justify-between gap-4" data-id="element-33">
              <div className="flex-1" data-id="element-34">
                <div className="flex items-center gap-2 mb-3" data-id="element-35">
                  <div className="w-10 h-10 bg-emerald-200 rounded-lg flex items-center justify-center" data-id="element-36">
                    <TrendingDown className="w-6 h-6 text-emerald-700" data-id="element-37" />
                  </div>
                  <div data-id="element-38">
                    <p className="text-sm text-emerald-700 font-medium" data-id="element-39">
                      MEILLEUR CHOIX
                    </p>
                    <p className="text-xl font-bold text-emerald-900" data-id="element-40">
                      {PROVIDERS[bestProvider.provider].name}
                    </p>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-41">
                  <div className="flex justify-between text-sm" data-id="element-42">
                    <span className="text-emerald-700" data-id="element-43">Coût total:</span>
                    <span className="font-bold text-emerald-900" data-id="element-44">
                      {formatXOF(bestProvider.totalCost)}
                    </span>
                  </div>
                  {savings > 0 && <div className="flex justify-between text-sm" data-id="element-45">
                      <span className="text-emerald-700" data-id="element-46">Économie:</span>
                      <span className="font-bold text-emerald-900" data-id="element-47">
                        {formatXOF(savings)}
                      </span>
                    </div>}
                </div>
              </div>
              <img src={PROVIDERS[bestProvider.provider].logoUrl} alt={PROVIDERS[bestProvider.provider].name} className="w-16 h-16 rounded-xl object-contain bg-white p-2 shadow-sm" data-id="element-48" />
            </div>
          </div>

          {/* Provider Comparison Table */}
          <div data-id="element-49">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-50">
              Comparaison des providers
            </h3>
            <div className="space-y-3" data-id="element-51">
              {comparisons.map((comparison, index) => {
              const providerInfo = PROVIDERS[comparison.provider];
              const isBest = index === 0;
              const difference = comparison.totalCost - bestProvider.totalCost;
              return <motion.div key={comparison.provider} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.05
              }} className={`rounded-xl border-2 overflow-hidden ${isBest ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`} data-id="element-52">
                    <div className="p-4" data-id="element-53">
                      <div className="flex items-center justify-between mb-3" data-id="element-54">
                        <div className="flex items-center gap-3" data-id="element-55">
                          <img src={providerInfo.logoUrl} alt={providerInfo.name} className="w-12 h-12 rounded-lg object-contain bg-white p-1.5 shadow-sm" data-id="element-56" />
                          <div data-id="element-57">
                            <div className="flex items-center gap-2" data-id="element-58">
                              <span className={`font-bold ${isBest ? 'text-emerald-900' : 'text-gray-900'}`} data-id="element-59">
                                {providerInfo.name}
                              </span>
                              {isBest && <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-bold" data-id="element-60">
                                  LE PLUS ÉCONOMIQUE
                                </span>}
                            </div>
                            <p className="text-sm text-gray-600" data-id="element-61">
                              {providerInfo.country === 'ALL' ? 'Tous pays' : providerInfo.country}
                            </p>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-62">
                          <p className={`text-2xl font-bold ${isBest ? 'text-emerald-900' : 'text-gray-900'}`} data-id="element-63">
                            {formatXOF(comparison.totalCost)}
                          </p>
                          {!isBest && difference > 0 && <p className="text-sm text-red-600 font-medium" data-id="element-64">
                              +{formatXOF(difference)}
                            </p>}
                        </div>
                      </div>

                      {/* Breakdown */}
                      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200" data-id="element-65">
                        <div data-id="element-66">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-67">
                            Taux de change
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-68">
                            {currency === 'XOF' ? '1:1' : `${comparison.displayedFxRate} XOF`}
                          </p>
                        </div>
                        <div data-id="element-69">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-70">Frais</p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-71">
                            {comparison.fee === 'not_defined' ? 'Non communiqué' : formatXOF(comparison.fee)}
                          </p>
                        </div>
                        <div data-id="element-72">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-73">
                            Montant converti
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-74">
                            {formatXOF(comparison.totalCost - (comparison.fee === 'not_defined' ? 0 : comparison.fee))}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Note */}
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-75">
            <p className="text-xs text-gray-600" data-id="element-76">
              💡 <strong data-id="element-77">Note:</strong> Ces calculs utilisent les taux de change
              actuels. Les coûts réels peuvent varier légèrement. Vérifiez
              toujours le montant final dans l'application de votre provider
              avant de confirmer le paiement.
            </p>
          </div>

          {/* Close Button */}
          <div className="mt-6" data-id="element-78">
            <button onClick={onClose} className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium" data-id="element-79">
              Fermer
            </button>
          </div>
        </div>
      </motion.div>
    </div>;
}